import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ConsultantNode } from '../../types/career';
import { useCanvasStore } from '../../store/useCanvasStore';
import { flattenTree, findPathToNode } from '../../utils/treeUtils';
import { Badge } from '../ui/Badge';
import { formatAE } from '../../utils/formatters';
import { Search, X, User } from 'lucide-react';

export interface SearchBarProps {
  rootNode: ConsultantNode;
  onSelectNode?: (nodeId: string) => void;
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  rootNode,
  onSelectNode,
  className = '',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setSearchQuery = useCanvasStore((state) => state.setSearchQuery);
  const setSelectedNodeId = useCanvasStore((state) => state.setSelectedNodeId);
  const setHighlightedPath = useCanvasStore((state) => state.setHighlightedPath);

  const allNodes = useMemo(() => flattenTree(rootNode), [rootNode]);

  // Filtered search results
  const filteredNodes = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return [];

    return allNodes
      .filter(
        (node) =>
          node.name.toLowerCase().includes(query) ||
          node.id.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [allNodes, inputValue]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (node: ConsultantNode) => {
    setInputValue(node.name);
    setSearchQuery(node.name);
    setSelectedNodeId(node.id);
    const path = findPathToNode(rootNode, node.id);
    setHighlightedPath(path);
    onSelectNode?.(node.id);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    setSearchQuery('');
    setSelectedNodeId(null);
    setHighlightedPath([]);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative flex items-center gap-2 ${className}`}>
      {/* Search Input Container */}
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
          <Search className="w-4 h-4" />
        </div>

        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setSearchQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Пошук консультанта за ім'ям або ID..."
          className="
            w-full pl-9 pr-8 py-2 text-base sm:text-sm rounded-xl
            bg-slate-900/90 text-text-primary placeholder:text-text-muted
            border border-white/10 focus:border-accent-emerald/50 focus:outline-none
            focus:ring-2 focus:ring-accent-emerald/20 transition-all min-h-[42px]
          "
        />

        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-text-muted hover:text-text-primary"
            aria-label="Очистити пошук"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Live Search Suggestions Dropdown */}
        {isOpen && filteredNodes.length > 0 && (
          <div
            className="
              absolute top-full left-0 right-0 mt-1.5 z-50 rounded-xl
              bg-[#0F172A] border border-white/15 shadow-[0_15px_35px_rgba(0,0,0,0.6)]
              backdrop-blur-xl overflow-hidden divide-y divide-white/5 max-h-64 overflow-y-auto custom-scrollbar
            "
          >
            {filteredNodes.map((node) => (
              <button
                key={node.id}
                type="button"
                onClick={() => handleSelect(node)}
                className="
                  w-full px-3.5 py-2.5 text-left flex items-center justify-between gap-2
                  hover:bg-surface-hover/80 transition-colors cursor-pointer text-xs
                "
              >
                <div className="flex items-center gap-2.5 truncate">
                  <div className="w-6 h-6 rounded-md bg-surface-hover flex items-center justify-center text-accent-emerald flex-shrink-0">
                    <User className="w-3.5 h-3.5" />
                  </div>
                  <div className="truncate">
                    <div className="font-semibold text-text-primary truncate">{node.name}</div>
                    <div className="text-[10px] text-text-muted font-mono">{node.id}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-mono text-text-secondary text-[11px]">
                    {formatAE(node.totalMonthlyAE)}
                  </span>
                  <Badge level={node.level} size="sm">
                    Р{node.level}
                  </Badge>
                  {!node.isActive && (
                    <span className="text-[10px] text-amber-400 font-medium">dormant</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
